// VanSchedulerService.js
// Production-grade van scheduling logic for Lasmari Transfers
// Uses Supabase as backend. All times are UTC ISO strings.

import { supabase } from './SupabaseService';

// Helper: Adds minutes to a UTC ISO string, returns new ISO string
function addMinutesUTC(isoString, minutes) {
  const date = new Date(isoString);
  date.setUTCMinutes(date.getUTCMinutes() + minutes);
  return date.toISOString();
}

// Helper: Checks if two time windows overlap
function isOverlap(aStart, aEnd, bStart, bEnd) {
  return new Date(aStart) < new Date(bEnd) && new Date(aEnd) > new Date(bStart);
}

/**
 * Van Scheduler Service - Handles van availability and assignment (NO DRIVERS)
 */

const VanSchedulerService = {
  /**
   * Get all available vans for a given start time and duration (in minutes, one-way)
   * Includes return trip (duration x2).
   * @param {string} startTime - UTC ISO string
   * @param {number} durationMinutes - one-way duration in minutes
   * @param {number} requiredPassengers - number of passengers for this booking
   * @returns {Promise<Array>} - List of available van records
   */
  async getAvailableVans(startTime, durationMinutes, requiredPassengers = 1) {
    console.log(`🔍 VanSchedulerService.getAvailableVans called with:`, {
      startTime,
      durationMinutes,
      requiredPassengers,
      endTime: addMinutesUTC(startTime, durationMinutes * 2)
    });
    
    // DEBUG: Log with more visibility to make sure passenger validation is being triggered
    console.log(`⚠️ VALIDATION CHECK: Checking if we can accommodate ${requiredPassengers} passengers`);
    
    const endTime = addMinutesUTC(startTime, durationMinutes * 2);
    
    // NEW: Calculate how many vans are needed for the passenger count
    const SEATS_PER_VAN = 7;
    const requiredVans = Math.ceil(requiredPassengers / SEATS_PER_VAN);
    console.log(`🧮 Need ${requiredVans} vans for ${requiredPassengers} passengers (${SEATS_PER_VAN} seats per van)`);
    
    // 1. Fetch all available vans (changed from 'active' to 'available')
    const { data: vans, error: vansError } = await supabase
      .from('vans')
      .select('*')
      .eq('status', 'available');
    if (vansError) throw vansError;

    console.log(`📊 Found ${vans ? vans.length : 0} vans with 'available' status:`, vans);
    
    // NEW: Check if we have enough vans in the fleet for this passenger count
    if (vans.length < requiredVans) {
      console.error(`❌ CAPACITY ERROR: Need ${requiredVans} vans for ${requiredPassengers} passengers, but only have ${vans.length} vans in the fleet`);
      const maxCapacity = vans.length * SEATS_PER_VAN;
      const error = new Error(`Booking failed: We need ${requiredVans} vans for ${requiredPassengers} passengers, but our fleet only has ${vans.length} vans (maximum capacity: ${maxCapacity} passengers).`);
      error.code = 'INSUFFICIENT_FLEET_CAPACITY';
      throw error;
    }

    // 2. Fetch all assignments overlapping with requested window
    const { data: assignments, error: assignErr } = await supabase
      .from('booking_van_assignments')
      .select('van_id, start_time, end_time, status')
      .in('status', ['assigned', 'in_progress'])
      .or(`start_time.lt.${endTime},end_time.gt.${startTime}`);
    if (assignErr) throw assignErr;

    console.log(`📋 Found ${assignments ? assignments.length : 0} overlapping assignments:`, assignments);

    // 3. Fetch all van blocks overlapping with requested window
    const { data: blocks, error: blocksErr } = await supabase
      .from('van_availability_blocks')
      .select('van_id, start_time, end_time')
      .or(`start_time.lt.${endTime},end_time.gt.${startTime}`);
    if (blocksErr) throw blocksErr;

    console.log(`🚫 Found ${blocks ? blocks.length : 0} overlapping blocks:`, blocks);

    // 4. Filter vans
    const unavailableVanIds = new Set();
    for (const a of assignments) {
      if (isOverlap(a.start_time, a.end_time, startTime, endTime)) {
        console.log(`❌ Van ${a.van_id} blocked by assignment: ${a.start_time} - ${a.end_time}`);
        unavailableVanIds.add(a.van_id);
      }
    }
    for (const b of blocks) {
      if (isOverlap(b.start_time, b.end_time, startTime, endTime)) {
        console.log(`❌ Van ${b.van_id} blocked by manual block: ${b.start_time} - ${b.end_time}`);
        unavailableVanIds.add(b.van_id);
      }
    }
    
    const availableVans = vans.filter(van => !unavailableVanIds.has(van.id));
    
    // NEW: Check if we have enough AVAILABLE vans for this booking
    if (availableVans.length < requiredVans) {
      console.error(`❌ AVAILABILITY ERROR: Need ${requiredVans} vans, but only ${availableVans.length} are available at the requested time`);
      const error = new Error(`Booking failed: We need ${requiredVans} vans for ${requiredPassengers} passengers, but only ${availableVans.length} ${availableVans.length === 1 ? 'van is' : 'vans are'} available at the requested time.`);
      error.code = 'INSUFFICIENT_AVAILABLE_VANS';
      throw error;
    }
    
    console.log(`✅ Final result: ${availableVans.length} available vans out of ${vans.length} total vans`);
    console.log(`Available van IDs:`, availableVans.map(v => v.id));
    
    // Log a clear summary for debugging booking attempts
    console.log(`📊 AVAILABILITY SUMMARY:`);
    console.log(`   📍 Time slot: ${startTime} to ${endTime}`);
    console.log(`   👥 Passengers: ${requiredPassengers} (requires ${requiredVans} vans)`);
    console.log(`   🚐 Total vans in fleet: ${vans.length}`);
    console.log(`   ✅ Available vans: ${availableVans.length}`);
    console.log(`   🚫 Unavailable vans: ${unavailableVanIds.size}`);
    console.log(`   ${availableVans.length >= requiredVans ? '✅ ENOUGH VANS AVAILABLE' : '❌ NOT ENOUGH VANS AVAILABLE'}`);
    
    return availableVans;
  },

  /**
   * Assign the first available van to a booking (NO DRIVERS)
   * @param {string} bookingId
   * @returns {Promise<Object>} - Assignment record or null if none available
   */
  async assignVanToBooking(bookingId) {
    // 1. Fetch booking
    const { data: booking, error: bookingErr } = await supabase
      .from('bookings')
      .select('id, pickup_time, duration_minutes')
      .eq('id', bookingId)
      .single();
    if (bookingErr) throw bookingErr;
    const { pickup_time, duration_minutes } = booking;
    // 2. Find available vans
    const availableVans = await this.getAvailableVans(pickup_time, duration_minutes);
    if (!availableVans.length) return null;
    const van = availableVans[0];
    const startTime = pickup_time;
    const endTime = addMinutesUTC(pickup_time, duration_minutes * 2);
    // 3. Insert assignment (NO DRIVER)
    const { data: assignment, error: assignErr } = await supabase
      .from('booking_van_assignments')
      .insert([{
        van_id: van.id,
        booking_id: bookingId,
        start_time: startTime,
        end_time: endTime,
        status: 'assigned'
      }])
      .select()
      .single();
    if (assignErr) throw assignErr;
    return assignment;
  }
};

export default VanSchedulerService;
